import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
} from '@mui/material';

const About = () => {

  const milestones = [
    {
      year: '2020',
      title: 'Company Founded',
      description: 'InfHire was founded with a mission to simplify influencer marketing for both brands and content creators.'
    },
    {
      year: '2021',
      title: 'Seed Funding',
      description: 'Secured ₹20.75Cr in seed funding to build the platform and expand the team.'
    },
    {
      year: '2022',
      title: 'Platform Launch',
      description: 'Official launch of the InfHire platform with 500+ influencers and 100+ brands.'
    },
    {
      year: '2023',
      title: 'Series A Funding',
      description: 'Raised ₹83Cr in Series A funding to accelerate growth and add new features.'
    },
    {
      year: '2024',
      title: 'Global Expansion',
      description: 'Expanded operations to Europe and Asia, with support for multiple languages and currencies.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            component="h1"
            variant="h2"
            align="center"
            color="text.primary"
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            About InfHire
          </Typography>
          <Typography
            variant="h5"
            align="center"
            color="text.secondary"
            paragraph
            sx={{ maxWidth: 800, mx: 'auto', mb: 6 }}
          >
            We're on a mission to transform influencer marketing by creating meaningful connections between brands and content creators.
          </Typography>
        </Container>
      </Box>

      {/* Our Story Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
              Our Story
            </Typography>
            <Typography variant="body1" paragraph>
              InfHire was born out of frustration with the existing influencer marketing landscape. Our founders, Sarah and Michael, saw firsthand how difficult it was for brands to find the right influencers and for content creators to connect with relevant opportunities.
            </Typography>
            <Typography variant="body1" paragraph>
              In 2020, they decided to build a solution that would make influencer marketing more accessible, transparent, and effective for everyone involved. What started as a simple matching platform has evolved into a comprehensive suite of tools for discovery, collaboration, and campaign management.
            </Typography>
            <Typography variant="body1">
              Today, InfHire connects thousands of brands and influencers worldwide, facilitating authentic partnerships that drive real results. Our "Connect & Collab" philosophy guides everything we do, as we continue to innovate and improve the platform based on user feedback.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
              alt="Team collaboration"
              sx={{
                width: '100%',
                borderRadius: 2,
                boxShadow: 3,
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Mission & Values Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 600 }}
          >
            Our Mission & Values
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #eaeaea',
                  borderRadius: 2,
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 2
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    Mission
                  </Typography>
                  <Typography variant="body1">
                    To create a transparent and efficient ecosystem where brands and influencers can connect, collaborate, and grow together.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: '1px solid #eaeaea',
                  borderRadius: 2
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    Core Values
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Authenticity</Typography>
                      <Typography variant="body2" paragraph>
                        We believe in fostering genuine connections that resonate with audiences.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Transparency</Typography>
                      <Typography variant="body2" paragraph>
                        We provide clear information about pricing, performance, and expectations.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Innovation</Typography>
                      <Typography variant="body2" paragraph>
                        We continuously improve our platform to meet evolving industry needs.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Inclusivity</Typography>
                      <Typography variant="body2">
                        We welcome creators of all sizes and backgrounds to our platform.
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Timeline Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            align="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 600 }}
          >
            Our Journey
          </Typography>

          <Box sx={{ position: 'relative' }}>
            {/* Timeline line */}
            <Box
              sx={{
                position: 'absolute',
                left: { xs: 16, md: '50%' },
                transform: { xs: 'none', md: 'translateX(-50%)' },
                top: 0,
                bottom: 0,
                width: 2,
                bgcolor: 'primary.main',
                zIndex: 0
              }}
            />

            {/* Timeline items */}
            {milestones.map((milestone, index) => (
              <Box
                key={milestone.year}
                sx={{
                  position: 'relative',
                  mb: 6,
                  ml: { xs: 5, md: 0 }
                }}
              >
                {/* Year marker */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: { xs: -40, md: '50%' },
                    transform: { xs: 'none', md: 'translateX(-50%)' },
                    top: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    zIndex: 1
                  }}
                >
                  {milestone.year}
                </Box>

                {/* Content */}
                <Box
                  sx={{
                    ml: { xs: 0, md: index % 2 === 0 ? '50%' : 0 },
                    mr: { xs: 0, md: index % 2 === 0 ? 0 : '50%' },
                    pl: { xs: 0, md: index % 2 === 0 ? 4 : 0 },
                    pr: { xs: 0, md: index % 2 === 0 ? 0 : 4 },
                    textAlign: { xs: 'left', md: index % 2 === 0 ? 'left' : 'right' }
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    {milestone.title}
                  </Typography>
                  <Typography variant="body1">
                    {milestone.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default About;
